import {
    Observable, of,
    AsyncSubject, map, catchError
} from "rxjs";

import {
    DestroyRef, Injectable, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    HttpClient, HttpHeaders
} from "@angular/common/http";

import {
    User, UserSearchFilterOptions, SearchResponse
} from "@shopliftr/common-js/shared";
import {
    ISearchSort
} from "@shopliftr/common-js/data";
import { throwError } from "rxjs";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";
import { AuthenticationToken } from "../../../models/authentication-token";
import { IInternalApiConfig } from "../../../interfaces/internal-api-config";
import { COMMON_ERROR_HANDLER } from "../../../../exceptions/constants/exception-constants";
import { INTERNAL_API_APP_CONFIG } from "../../../constants/iapi-app-tokens";
import { ExpiredTokenException } from "../../../../exceptions/expired-token-exception";
import { BadRequestException } from "../../../../exceptions/bad-request-exception";
import { UserApiClientService } from "../../api-client/user/user-api-client.service";

/**
 * Used to read and manage User data in Internal API
 */
@Injectable({
    providedIn: "root"
})
export class UserRequestService {
    private readonly _httpClient = inject(HttpClient);
    private readonly _appConfig = inject<IInternalApiConfig>(INTERNAL_API_APP_CONFIG);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);
    private readonly _userClient = inject(UserApiClientService);

    /**
     * The reference for when this service is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * The Internal API endpoint used for user CRUD operations (v1)
     */
    private readonly _userUrl: string;

    /**
     * Internal cache of User.id to User.name
     *
     * It is populated every time a user is retrieved in a request
     *
     * It is used by the getUserName function
     */
    private readonly _userIdToName: Map<string, string>;

    /**
     * Internal cache of User.id to the active Observable used in getUserName function
     *
     * It is populated every time a getUserName request is made.
     *
     * The cached observable is cleared when completed or errored
     *
     * It is used by the getUserName function
     */
    private readonly _userIdToName$: Map<string, AsyncSubject<string>>;

    constructor() {

        this._userUrl = `${this._appConfig.apiUrl}/v1/user`;
        this._userIdToName = new Map();
        this._userIdToName$ = new Map();
    }

    /**
     * Returns the user name for the userId.
     *
     * If the user name is undefined, the observable will emit undefined.
     *
     * Execution of the request occurs when the function is called, not when the observable is subscribed
     *
     * Used by the UserNamePipe
     *
     * Usernames are cached anytime users are retrieved by the service users are retrieved
     *
     * To prevent multiple request for the same userId, the Observables are cached until they either error or complete
     */
    getUserName$(userId: string): Observable<string> {

        if (!userId) {
            return of(undefined);
        }

        // Check the name cache first
        const userName = this._userIdToName.get(userId);
        if (userName) {
            return of(userName);
        }

        // We cache the observable to prevent multiple requrests from being fired
        let userName$ = this._userIdToName$.get(userId);
        if (!userName$) {

            // Returning a cached AsyncSubject that subscribes to getUser, ensures that the request is only made once
            userName$ = new AsyncSubject();
            this.getUser$(userId).pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe({
                    next: (user) => userName$.next(user.name),
                    error: (error: unknown) => {
                        // Remove the cached observable on error
                        // So we can rexecute the request on the next call
                        this._userIdToName$.delete(userId);
                        userName$.error(this._errorHandler.handleServiceError(error, `Failed to get user name for ${userId}`));
                    },
                    complete: () => {
                        // Removes the cached observable on completion
                        // The name will be cached in the names cache now
                        this._userIdToName$.delete(userId);
                        userName$.complete();
                    }
                });

            this._userIdToName$.set(userId, userName$);
        }
        return userName$.asObservable();
    }

    /**
     * Get the user by id
     */
    getUser$(userId: string): Observable<User> {

        if (userId) {

            const url = `${this._userUrl}/${userId}`;
            return this._httpClient.get(url).pipe(map((responseBody: unknown): User => {
                const user = User.deserialize(responseBody);
                this._cacheUserName(user);
                return user;
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error, `Failed to get user for ${userId}`));
            }));
        }
        else {
            return throwError(() => new InvalidParametersException("userId is required"));
        }
    }

    /**
     * Get all Users readable by the Authenticated User
     */
    getUsers$(): Observable<Array<User>> {

        return this._httpClient.get<Record<"users", Array<unknown>>>(this._userUrl).pipe(map((responseBody): Array<User> => {

            const users = User.deserializeArray(responseBody.users);
            users.forEach((user) => this._cacheUserName(user));
            return users;
        }),
        catchError((error: unknown) => {

            return throwError(() => this._errorHandler.handleServiceError(error, `Failed to get users`));
        }));
    }

    /**
     * Return the users for a specific Team. If no TeamId is provided it will return all the Users on the authenticated user's team.
     *
     * Delegates to {@link UserApiClientService.getAll$} using a {@link UserSearchFilterOptions} with an optional `teamId`.
     */
    getUsersForTeam$(teamId?: string): Observable<Array<User>> {

        const filterOptions = new UserSearchFilterOptions();
        if (teamId) {
            filterOptions.teamId = teamId;
        }
        return this._userClient.getAll$(filterOptions).pipe(
            map((users: Array<User>) => {
                users.forEach((user) => this._cacheUserName(user));
                return users;
            })
        );
    }

    /**
     * Search for users using v2 API with filter options.
     *
     * Delegates to {@link UserApiClientService.search$} and caches user names for all results.
     *
     * @see UserApiClientService.search$
     */
    search$(
        filterOptions: UserSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<User>> {

        return this._userClient.search$(filterOptions, page, pageSize, sort).pipe(
            map((response: SearchResponse<User>) => {
                response.results.forEach((user) => this._cacheUserName(user));
                return response;
            })
        );
    }

    /**
     * Get all users matching the given filter options.
     *
     * Delegates to {@link UserApiClientService.getAll$} and caches user names for all results.
     *
     * @see UserApiClientService.getAll$
     */
    getAll$(filterOptions: UserSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<User>> {

        return this._userClient.getAll$(filterOptions, sort).pipe(
            map((users: Array<User>) => {
                users.forEach((user) => this._cacheUserName(user));
                return users;
            })
        );
    }

    /**
     * Update the user. Will not update the password.
     *
     */
    updateUser$(user: User): Observable<User> {

        if (user) {
            const url = `${this._userUrl}/${user.id}`;
            const body = user.serialize();
            return this._httpClient.put(url, body).pipe(
                map((responseBody: unknown): User => {

                    const user = User.deserialize(responseBody);
                    this._cacheUserName(user);
                    return user;
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error, `Failed to update user ${user.id}`));
                })
            );
        }
        else {
            return throwError(() => new InvalidParametersException("user is required"));
        }
    }

    /**
     * Will submit the request to deactivate a user
     *
     */
    deactivateUser$(id: string): Observable<boolean> {

        if (id) {
            return this._httpClient.delete<Record<"success", boolean>>(`${this._userUrl}/${id}`).pipe(
                map((responseBody): boolean => {

                    return responseBody.success;
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error, `Failed to deactivate user ${id}`));
                })
            );
        }
        else {
            return throwError(() => new InvalidParametersException("id is required"));
        }
    }

    /**
     * A shorthand method to update the 'active' field for a user
     */
    activateUser$(user: User): Observable<User> {

        if (user) {
            const workingUser = user.clone();
            workingUser.active = true;
            return this.updateUser$(workingUser).pipe(
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error, `Failed to activate user ${user.id}`));
                })
            );
        }
        else {
            return throwError(() => new InvalidParametersException("user is required"));
        }
    }

    /**
     * Set the current users password. If using the currently logged in user you will have to provide the currentPassword.
     * If you are using the temporary token, the currentPassword is not required.
     */
    setPassword$(password: string,
        currentPassword?: string,
        temporaryAuthToken?: AuthenticationToken): Observable<boolean> {

        if (password) {

            if (!currentPassword && !temporaryAuthToken) {
                return throwError(() => new InvalidParametersException("currentPassword or temporaryAuthToken is required"));
            }
            const fields: any = {
                password: password,
                passwordConfirmation: password
            };
            if (currentPassword) {
                fields["currentPassword"] = currentPassword;
            }
            let headers = new HttpHeaders();
            if (temporaryAuthToken) {
                if (temporaryAuthToken.isExpired) {
                    return throwError(() => new ExpiredTokenException());
                }
                if (temporaryAuthToken.isExpired || !temporaryAuthToken.isTemporary) {
                    return throwError(() => new InvalidParametersException("Temporary auth token is invalid"));
                }
                // Use the temporary token for authorizations
                headers = headers.append("Authorization", temporaryAuthToken.headerValue);
            }

            return this._httpClient.put<Record<"success", boolean>>(`${this._userUrl}/password`, fields, { headers: headers }).pipe(
                map((responseBody): boolean => {

                    return responseBody.success;
                }),
                catchError((error: unknown) => {

                    if (error instanceof BadRequestException) {
                        return throwError(() => error);
                    }
                    return throwError(() => this._errorHandler.handleServiceError(error, "Failed to set user password"));
                })
            );
        }
        else {
            return throwError(() => new InvalidParametersException("password is required"));
        }
    }

    /**
     * Create a user and sends an invitation to the user.
     * If no teamId is provided the user will be added to the current user's team
     */
    create$(user: User): Observable<User> {

        if (user) {
            // POST /v1/user rejects `name` ("name" is not allowed): common-js persists User.name since MGD-747, but
            // internal-api's v1 create schema doesn't accept it, unlike v1 update, which strips it. The server
            // doesn't read it on create, so it is left out of the payload.
            const fields = user.serialize() as Record<string, unknown>;
            delete fields.name;

            return this._httpClient.post(this._userUrl, fields).pipe(
                map((responseBody: unknown): User => {

                    const user = User.deserialize(responseBody);
                    this._cacheUserName(user);
                    return user;
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error, "Failed to create user"));
                })
            );
        }
        else {
            return throwError(() => new InvalidParametersException("user is required"));
        }
    }

    /**
     * Sends a password reset email to the provided email.
     *
     */
    forgotPassword$(email: string): Observable<void> {

        if (email) {
            return this._httpClient.post(this._userUrl + "/forgot-password", { email: email }).pipe(
                map((): void => { }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error, "Failed to send forgot password request"));
                })
            );
        }
        else {
            return throwError(() => new InvalidParametersException("email is required"));
        }
    }

    /**
     * Add the user name to the cache
     */
    private _cacheUserName(user: User): void {

        this._userIdToName.set(user.id, user.name);
    }
}
